import { useState, useMemo } from "react";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { ApiFactory } from "../../core/config/ApiFactory";
import { ICupom } from "../interface/ICupom";
import { TipoCupom } from "../enum/TipoCupom";
import { TipoMarketplace } from "../../marketplace/enum/TipoMarketplace";

interface CriarCupomFicticioProps {
    codigo: string;
    tipo: TipoCupom;
    valor: number;
}

export const useCuponsPedido = () => {
    const [loading, setLoading] = useState(false);
    const snackbar = useSnackbar();
    const apiPedidos = useMemo(() => ApiFactory.getApi("MS_PEDIDOS"), []);

    const buscarCuponsValidos = async (): Promise<ICupom[]> => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.get("/cupons?ativo=true");
            
            if (!data || !Array.isArray(data)) {
                return [];
            }

            // Filtrar cupons que têm marketplace PDV no array
            const cuponsValidos = data.filter((cupom: ICupom) => {
                // Se não tem marketplaces definidos, não é válido para PDV
                if (!cupom.marketplaces || cupom.marketplaces.length === 0) {
                    return false;
                }

                // Verificar se tem marketplace PDV
                const temMarketplacePDV = cupom.marketplaces.some(
                    (m) => m.tipo === TipoMarketplace.PDV
                );

                if (!temMarketplacePDV) {
                    return false;
                }

                // Verificar se está ativo
                if (cupom.ativo === false) {
                    return false;
                }

                // Verificar data de validade se existir
                if (cupom.dataValidade) {
                    const dataValidade = new Date(cupom.dataValidade);
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);
                    dataValidade.setHours(0, 0, 0, 0);
                    
                    if (dataValidade < hoje) {
                        return false;
                    }
                }

                return true;
            });

            return cuponsValidos;
        } catch (error: any) {
            snackbar(
                error?.response?.data?.message ||
                    "Ocorreu um erro ao buscar os cupons",
                { severity: "error" }
            );
            return [];
        } finally {
            setLoading(false);
        }
    };

    const aplicarCupom = async (
        pedidoUuid: string | undefined,
        cupom: ICupom
    ): Promise<boolean> => {
        // Se não tem pedidoUuid, é um novo pedido (retorna true para aplicar localmente)
        if (!pedidoUuid) {
            return true;
        }

        // Se tem pedidoUuid, é edição (chama API)
        setLoading(true);
        try {
            // Sempre enviar o objeto completo do cupom
            const payload = {
                cupom: {
                    uuid: cupom.uuid,
                    codigo: cupom.codigo,
                    tipo: cupom.tipo,
                    valor: cupom.valor,
                    nome: cupom.nome || `Cupom ${cupom.codigo}`,
                    descricao: cupom.descricao || "Cupom criado manualmente",
                    marketplaces: cupom.marketplaces || [{ tipo: TipoMarketplace.PDV }],
                    ...(cupom.programaFidelidadeUuid && { programaFidelidadeUuid: cupom.programaFidelidadeUuid }),
                }
            };

            const { data } = await apiPedidos.post(`/${pedidoUuid}/cupons`, payload);

            if (data) {
                snackbar("Cupom aplicado com sucesso", { severity: "success" });
                return true;
            }

            return false;
        } catch (error: any) {
            snackbar(
                error?.response?.data?.message ||
                    "Ocorreu um erro ao aplicar o cupom",
                { severity: "error" }
            );
            return false;
        } finally {
            setLoading(false);
        }
    };

    const removerCupom = async (
        pedidoUuid: string | undefined,
        codigo: string,
        tipo: TipoCupom
    ): Promise<boolean> => {
        // Se não tem pedidoUuid, é um novo pedido (retorna true para remover localmente)
        if (!pedidoUuid) {
            return true;
        }

        // Se tem pedidoUuid, é edição (chama API)
        setLoading(true);
        try {
            const { data } = await apiPedidos.delete(`/${pedidoUuid}/cupons`, {
                data: { codigo, tipo },
            });

            if (data) {
                snackbar("Cupom removido com sucesso", { severity: "success" });
                return true;
            }

            return false;
        } catch (error: any) {
            snackbar(
                error?.response?.data?.message ||
                    "Ocorreu um erro ao remover o cupom",
                { severity: "error" }
            );
            return false;
        } finally {
            setLoading(false);
        }
    };

    const criarCupomFicticio = (
        dados: CriarCupomFicticioProps
    ): ICupom | null => {
        // Validação mínima
        if (!dados.codigo || dados.codigo.trim() === "") {
            snackbar("O código do cupom é obrigatório", { severity: "error" });
            return null;
        }

        if (!dados.tipo || !Object.values(TipoCupom).includes(dados.tipo)) {
            snackbar("O tipo do cupom é inválido", { severity: "error" });
            return null;
        }

        if (!dados.valor || dados.valor <= 0) {
            snackbar("O valor do cupom deve ser maior que zero", {
                severity: "error",
            });
            return null;
        }

        const cupomFicticio: ICupom = {
            codigo: dados.codigo.trim(),
            tipo: dados.tipo,
            valor: dados.valor,
            nome: `Cupom ${dados.codigo}`,
            descricao: "Cupom criado manualmente",
            ativo: true,
            marketplaces: [{ tipo: TipoMarketplace.PDV }],
        };

        return cupomFicticio;
    };

    return {
        loading,
        buscarCuponsValidos,
        aplicarCupom,
        removerCupom,
        criarCupomFicticio,
    };
};
